import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getProducts } from "../services/productService";
import getOptimizedUrl from "../utils/imageUtils";
import image1 from "./assets/top.webp";
import image2 from "./assets/middle.webp";
import image3 from "./assets/bottom.webp";

const fallbackFanoutItems = [
  {
    id: "shelf",
    title: "Arco Shelf Metal",
    alt: "Arco Shelf Metal",
    image: image3,
    price: "$ 4,200.00 USD",
    layer: "left",
    zClass: "z-10",
  },
  {
    id: "sofa",
    title: "Arco Sofa Metal",
    alt: "Arco Sofa Metal",
    image: image2,
    price: "$ 7,500.00 USD",
    layer: "center",
    zClass: "z-20",
  },
  {
    id: "stool",
    title: "Arco Stool Metal",
    alt: "Arco Stool Metal",
    image: image1,
    price: "$ 1,500.00 USD",
    layer: "right",
    zClass: "z-30",
  },
];

const slotOrder = ["left", "center", "right"];
const slotZIndex = {
  left: "z-10",
  center: "z-20",
  right: "z-30",
};

const formatPrice = (product) => {
  const amount = Number(product?.discountedPrice ?? product?.mrp);
  if (!Number.isFinite(amount) || amount <= 0) return "";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export default function SplitImageScroll() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [showMobileViewAll, setShowMobileViewAll] = useState(false);
  const [fanoutItems, setFanoutItems] = useState(fallbackFanoutItems);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Image translations (-100% and 100% move the images perfectly edge-to-edge)
  const xRight = useTransform(scrollYProgress, [0, 0.9], ["0%", "100%"]);
  const xLeft = useTransform(scrollYProgress, [0, 0.9], ["0%", "-100%"]);
  const contentOpacity = useTransform(scrollYProgress, [0.9, 1], [0, 1]);
  const contentY = useTransform(scrollYProgress, [0.9, 1], [30, 0]);

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (latest) => {
      const next = latest >= 0.98;
      setShowMobileViewAll((prev) => (prev === next ? prev : next));
    });

    return () => unsubscribe();
  }, [scrollYProgress]);

  useEffect(() => {
    let mounted = true;

    const notifyHomeContentUpdated = () => {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("home:content-updated"));
      }
    };

    const loadFanoutProducts = async () => {
      try {
        const cmsProducts = await getProducts({ shownInFanout: true });
        if (!mounted) return;

        const slotMap = {};
        (cmsProducts || []).forEach((product) => {
          const slot = String(product?.fanoutPosition || "").toLowerCase();
          if (!slotOrder.includes(slot)) return;
          if (slotMap[slot]) return;
          slotMap[slot] = product;
        });

        const mapped = slotOrder.map((slot, index) => {
          const cmsProduct = slotMap[slot];
          const fallback = fallbackFanoutItems[index];

          if (!cmsProduct) {
            return {
              ...fallback,
              layer: slot,
              zClass: slotZIndex[slot],
            };
          }

          return {
            id: cmsProduct._id,
            title: cmsProduct.product || fallback.title,
            alt: cmsProduct.product || fallback.alt,
            image: cmsProduct.images?.[0] || fallback.image,
            price: formatPrice(cmsProduct) || fallback.price,
            slug: cmsProduct.slug || "",
            layer: slot,
            zClass: slotZIndex[slot],
          };
        });

        setFanoutItems(mapped);
      } catch (error) {
        console.error("Failed to load fanout products", error);
      } finally {
        if (mounted) {
          notifyHomeContentUpdated();
          setTimeout(notifyHomeContentUpdated, 200);
        }
      }
    };

    loadFanoutProducts();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="w-full px-4 relative z-10 -mb-[55vh] md:-mb-[20vh]">
      
      {/* TALL parent container ensures a slow, luxurious scroll speed */}
      <div ref={containerRef} className="h-[200vh] md:h-[250vh] relative">
        
        {/* THE FIX 2: Restored to h-screen so the Framer Motion timing is perfect.
            Added pointer-events-none so if the user taps the empty space below the 
            images, they interact with the "Discover Collection" section sliding underneath.
        */}
        <div className="sticky top-0 h-screen w-full flex justify-center pt-[10vh] overflow-visible pointer-events-none">
          <div className="absolute inset-x-0 top-[22.5vh] md:top-[40vh] -translate-y-1/2 flex items-center justify-between  pointer-events-none">
            <span
              className="home-body-text text-sm md:text-base font-bdogrotesk font-medium text-black leading-[1.0]"
              style={{ letterSpacing: "-0.028em" }}
            >
              Culture
            </span>
            <span
              className="home-body-text text-sm md:text-base font-bdogrotesk font-medium text-black leading-[1.0]"
              style={{ letterSpacing: "-0.028em" }}
            >
              Driven
            </span>
          </div>
          
          {/* pointer-events-auto ensures the images can still be clicked if needed later */}
          <div className="relative w-[calc((105%-0.1rem)/3)] md:w-[33vw] h-[25vh] md:h-[60vh] pointer-events-auto">
            {fanoutItems.map((item) => {
              const slideStyle =
                item.layer === "left"
                  ? { x: xLeft }
                  : item.layer === "right"
                    ? { x: xRight }
                    : undefined;

              return (
                <motion.div
                  key={item.id}
                  style={slideStyle}
                  className={`absolute inset-0 ${item.zClass} w-full h-full`}
                >
                  <img
                    src={getOptimizedUrl(item.image || "", 1200)}
                    alt={item.alt}
                    className="w-full h-full object-cover bg-white"
                  />

                  <motion.div
                    style={{ opacity: contentOpacity, y: contentY }}
                    className="absolute top-full left-0 w-full mt-2 md:mt-4 pointer-events-auto"
                  >
                    <h3 className="hidden md:block md:text-base font-medium md:mb-2 text-black text-left tracking-tight">
                      {item.title}
                    </h3>

                    <button
                      type="button"
                      onClick={() => item.slug && navigate(`/product/${item.slug}`)}
                      disabled={!item.slug}
                      className="hidden md:flex group mt-0 w-full bg-black text-white px-3 py-3 items-center justify-between text-[15px] font-semibold tracking-wide box-border"
                    >
                      <div className="relative h-5 overflow-hidden">
                        <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                          <span className="flex font-bdogrotesk h-5 items-center">
                            Shop now
                          </span>
                          <span className="flex h-5 items-center">Shop now</span>
                        </div>
                      </div>
                      <div className="relative h-5 overflow-hidden text-sm font-medium">
                        <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                          <span className="flex h-5 items-center justify-end">
                            {item.price}
                          </span>
                          <span className="flex h-5 items-center justify-end">
                            {item.price}
                          </span>
                        </div>
                      </div>
                    </button>
                  </motion.div>
                </motion.div>
              );
            })}

          </div>

          {showMobileViewAll && (
            <div className="absolute inset-x-0 top-[calc(10vh+25vh+2.5rem)] md:hidden flex justify-center pointer-events-auto z-40">
              <Link
                to="/store"
                className="home-body-text flex items-center space-x-3 text-sm font-medium text-black hover:underline hover:underline-offset-8 hover:decoration-2 transition-all group"
              >
                <span>View all</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}